import React from 'react';
import { Cartesian3DAxes } from '../common/Cartesian3DAxes.jsx';
import { DraggablePoint } from '../common/DraggablePoint.jsx';

export const PointToPlaneGeometryComponent = ({ params, onParamChange, svgRef, showCoordinates }) => {
  const {
    centerX = 200,
    centerY = 150,
    pointX = 250,
    pointY = 100,
    pointZ = 50,
    planeA = 1,
    planeB = 1,
    planeC = 1,
    planeD = -200,
    pointFill = "red",
    projectionFill = "blue",
    planeFill = "rgba(100, 150, 255, 0.3)",
    stroke = "black",
    strokeWidth = 2,
    showPoint = true,
    showProjection = true,
    showDistance = true,
    show3DAxes = true
  } = params;

  // Calculate projection of point onto plane
  // Plane equation: ax + by + cz + d = 0
  // Distance from point to plane: |ax0 + by0 + cz0 + d| / sqrt(a² + b² + c²)
  
  const normalLength = Math.sqrt(planeA * planeA + planeB * planeB + planeC * planeC);
  const distance = Math.abs(planeA * pointX + planeB * pointY + planeC * pointZ + planeD) / normalLength;
  
  // Projection point coordinates
  const t = -(planeA * pointX + planeB * pointY + planeC * pointZ + planeD) / (normalLength * normalLength);
  const projX = pointX + planeA * t;
  const projY = pointY + planeB * t;
  const projZ = pointZ + planeC * t;
  
  // Convert 3D to 2D for visualization (isometric projection)
  const to2D = (x, y, z) => ({
    x: centerX + (x - 200) * 0.866 + (z - 0) * 0.5,
    y: centerY - (y - 150) + (z - 0) * 0.3
  });
  
  const point2D = to2D(pointX, pointY, pointZ);
  const proj2D = to2D(projX, projY, projZ);
  
  // Plane vertices (as a parallelogram)
  const planeSize = 80;
  const planeCenter = to2D(centerX, centerY, 0);
  
  // Calculate plane orientation based on normal vector
  const planeVertices = [
    { x: planeCenter.x - planeSize, y: planeCenter.y - planeSize * 0.3 },
    { x: planeCenter.x + planeSize, y: planeCenter.y - planeSize * 0.3 },
    { x: planeCenter.x + planeSize * 0.7, y: planeCenter.y + planeSize * 0.5 },
    { x: planeCenter.x - planeSize * 1.3, y: planeCenter.y + planeSize * 0.5 }
  ];

  return (
    <g>
      {/* 3D Axes */}
      {show3DAxes && <Cartesian3DAxes centerX={centerX - 50} centerY={centerY + 80} size={50} />}
      
      {/* Plane */}
      <polygon
        points={planeVertices.map(v => `${v.x},${v.y}`).join(' ')}
        fill={planeFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Plane equation label */}
      <text
        x={planeCenter.x - 60}
        y={planeCenter.y - planeSize * 0.5}
        fontSize="11"
        fill="blue"
        fontWeight="bold"
      >
        Plane: {planeA}x + {planeB}y + {planeC}z + {planeD} = 0
      </text>
      
      {/* Normal vector */}
      <defs>
        <marker id="arrowPlaneNormal" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="purple" />
        </marker>
      </defs>
      <line
        x1={planeCenter.x}
        y1={planeCenter.y}
        x2={planeCenter.x + planeA * 30}
        y2={planeCenter.y - planeB * 30}
        stroke="purple"
        strokeWidth={strokeWidth}
        markerEnd="url(#arrowPlaneNormal)"
      />
      <text
        x={planeCenter.x + planeA * 35}
        y={planeCenter.y - planeB * 30}
        fontSize="10"
        fill="purple"
      >
        n⃗ = ({planeA}, {planeB}, {planeC})
      </text>
      
      {/* Point P */}
      {showPoint && (
        <>
          <circle
            cx={point2D.x}
            cy={point2D.y}
            r="6"
            fill={pointFill}
            stroke="darkred"
            strokeWidth={strokeWidth}
          />
          <text
            x={point2D.x + 10}
            y={point2D.y - 10}
            fontSize="12"
            fill={pointFill}
            fontWeight="bold"
          >
            P({pointX.toFixed(0)}, {pointY.toFixed(0)}, {pointZ.toFixed(0)})
          </text>
        </>
      )}
      
      {/* Projection P' */}
      {showProjection && (
        <>
          <circle
            cx={proj2D.x}
            cy={proj2D.y}
            r="6"
            fill={projectionFill}
            stroke="darkblue"
            strokeWidth={strokeWidth}
          />
          <text
            x={proj2D.x + 10}
            y={proj2D.y + 20}
            fontSize="12"
            fill={projectionFill}
            fontWeight="bold"
          >
            P'({projX.toFixed(0)}, {projY.toFixed(0)}, {projZ.toFixed(0)})
          </text>
        </>
      )}
      
      {/* Distance line */}
      {showDistance && (
        <>
          <line
            x1={point2D.x}
            y1={point2D.y}
            x2={proj2D.x}
            y2={proj2D.y}
            stroke="green"
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
          />
          <text
            x={(point2D.x + proj2D.x) / 2 + 10}
            y={(point2D.y + proj2D.y) / 2}
            fontSize="11"
            fill="green"
            fontWeight="bold"
          >
            d = {distance.toFixed(2)}
          </text>
        </>
      )}
      
      {/* Formula */}
      <text
        x={centerX - 150}
        y={centerY - 90}
        fontSize="11"
        fill="black"
        fontWeight="bold"
      >
        Distance = |ax₀ + by₀ + cz₀ + d| / √(a² + b² + c²)
      </text>
      
      {/* Draggable points */}
      {onParamChange && (
        <>
          <DraggablePoint
            cx={point2D.x}
            cy={point2D.y}
            label="P"
            coords={showCoordinates ? `(${pointX}, ${pointY}, ${pointZ})` : ''}
            onDrag={(newX, newY) => {
              // Convert back to 3D (simplified)
              onParamChange('pointX', Math.max(50, Math.min(350, newX)));
              onParamChange('pointY', Math.max(50, Math.min(250, newY)));
            }}
            svgRef={svgRef}
          />
        </>
      )}
    </g>
  );
};
